import React, { useState, useEffect, useCallback } from 'react';
import { toast } from '../../../components/shared/toast';
import LabelAvatarStack from '../../../components/shared/LabelAvatarStack';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import SharedHeader from '../../../components/shared/SharedHeader';
import Pagination from '../../../components/shared/Pagination';
import LoadingOverlay from '../../../components/shared/LoadingOverlay';
import EmptyState from '../../../components/shared/EmptyState';
import Loader from '../../../components/shared/Loader';
import { getBackendOrigin, getSecureAssetUrl } from '../../../utils/backendUrl';
import AddSongModal from './AddSongModal';
import { apiClient } from '../../../api/apiClient';
import { GET_OWNERSHIP_DETAILS_URL, DELETE_OWNERSHIP_URL } from '../../../api/ownership';

function DocumentDetails({ document, onBack, onEditDocument, onRefresh, onSelectSong, t }) {
  const [docDetail, setDocDetail] = useState(null);
  const [songs, setSongs] = useState([]);
  const [isDocLoading, setIsDocLoading] = useState(true);
  const [isLoadingSongs, setIsLoadingSongs] = useState(false);
  const [showTags, setShowTags] = useState(true);

  // Songs List Pagination & Search State
  const [songsPage, setSongsPage] = useState(1);
  const [songsTotalPages, setSongsTotalPages] = useState(1);
  const [songsTotalRecords, setSongsTotalRecords] = useState(0);
  const [songsSearchQuery, setSongsSearchQuery] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  // Modals state
  const [isAddSongOpen, setIsAddSongOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDownloadConfirmModal, setShowDownloadConfirmModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const docId = document ? document.id : null;

  // 1. Fetch document header once on mount / docId change
  const fetchDocHeader = useCallback(async () => {
    if (!docId) return;
    try {
      setIsDocLoading(true);
      const res = await apiClient(GET_OWNERSHIP_DETAILS_URL(docId));
      if (res) {
        setDocDetail(res);
      }
    } catch (err) {
      console.error('Error fetching document details:', err);
    } finally {
      setIsDocLoading(false);
    }
  }, [docId]);

  useEffect(() => {
    fetchDocHeader();
  }, [docId]);

  // 2. Fetch songs list dynamically when songsPage or songsSearchQuery changes
  const fetchSongsList = useCallback(async () => {
    if (!docId) return;
    try {
      setIsLoadingSongs(true);
      let queryParams = [`page=${songsPage}`, `limit=20`];
      if (songsSearchQuery.trim()) {
        queryParams.push(`search=${encodeURIComponent(songsSearchQuery.trim())}`);
      }

      const url = `${GET_OWNERSHIP_DETAILS_URL(docId)}?${queryParams.join('&')}`;
      const res = await apiClient(url);
      if (res) {
        setSongs(res.songs || []);
        setSongsTotalPages(res.totalPages || 1);
        setSongsTotalRecords(res.totalRecords !== undefined ? res.totalRecords : (res.songs || []).length);
      }
    } catch (err) {
      console.error('Error fetching songs list:', err);
      toast.error('Failed to load document songs.');
    } finally {
      setIsLoadingSongs(false);
    }
  }, [docId, songsPage, songsSearchQuery]);

  useEffect(() => {
    fetchSongsList();
  }, [docId, songsPage, songsSearchQuery]);

  // Handle Export Songs to CSV
  const handleExportSongs = async () => {
    if (!docId) return;
    try {
      setIsExporting(true);
      await new Promise((resolve) => setTimeout(resolve, 800));

      let queryParams = ['export=true'];
      if (songsSearchQuery.trim()) {
        queryParams.push(`search=${encodeURIComponent(songsSearchQuery.trim())}`);
      }

      const url = `${GET_OWNERSHIP_DETAILS_URL(docId)}?${queryParams.join('&')}`;
      const res = await apiClient(url);

      if (res && res.songs && res.songs.length > 0) {
        const headers = ['Song Name', 'Sinhala Name', 'Status', 'Artist', 'Lyrics', 'Music', 'Labels', 'Ownership Types', 'Ownership %', 'Notes / Cases', 'Conflicts'];
        const rows = res.songs.map(s => {
          const conflictVal = s.conflicts || s.conflict || (s.conflictCount > 0 ? `${s.conflictCount} Conflict` : 'No');
          return [
            s.rawName || s.name,
            s.nameSinhala || '',
            s.status || 'Active',
            s.artist || '—',
            s.lyrics || '—',
            s.music || '—',
            Array.isArray(s.labels) ? s.labels.map(l => l.name || l).join(', ') : '—',
            Array.isArray(s.ownershipTypes) ? s.ownershipTypes.join(', ') : (s.ownershipType || '—'),
            `${s.ownershipPercentage !== undefined ? s.ownershipPercentage : 0}%`,
            s.notes || 'No Cases Or Notes',
            conflictVal
          ];
        });

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const downloadUrl = URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        const docNameStr = (docDetail?.name || document?.name || 'document').replace(/[^a-z0-9]/gi, '_');
        link.setAttribute('href', downloadUrl);
        link.setAttribute('download', `ownership_songs_export_${docNameStr}_${new Date().toISOString().slice(0, 10)}.csv`);
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);

        toast.success('Ownership document songs exported successfully!');
      } else {
        toast.warning('No songs available to export.');
      }
    } catch (err) {
      console.error('Error exporting songs:', err);
      toast.error('Failed to export ownership document songs.');
    } finally {
      setIsExporting(false);
    }
  };

  // Handle Add Song submit
  const handleAddSongSubmit = async (songPayload) => {
    try {
      const response = await apiClient(`/ownership/${docId}/songs`, {
        method: 'POST',
        body: songPayload
      });
      if (response) {
        toast.success('Song added to ownership document successfully');
        fetchDetails();
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      console.error('Error adding song to document:', err);
      toast.error(err.message || 'Failed to add song to ownership document.');
    }
  };

  // Handle Delete Document confirm
  const handleConfirmDelete = async () => {
    if (!docId) return;
    try {
      setIsDeleting(true);
      const response = await apiClient(DELETE_OWNERSHIP_URL(docId), {
        method: 'DELETE'
      });
      if (response && response.success !== false) {
        toast.success('Ownership document deleted successfully');
        setShowDeleteModal(false);
        if (onRefresh) onRefresh();
        onBack();
      }
    } catch (err) {
      console.error('Error deleting ownership document:', err);
      toast.error(err.message || 'Failed to delete ownership document.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadDocument = async () => {
    const fileUrl = getSecureAssetUrl(
      docDetail?.document_url || docDetail?.documentUrl || document?.document_url || document?.documentUrl || `${getBackendOrigin()}/uploads/documents/sample-document.pdf`
    );
    
    try {
      toast.info('Downloading document...');
      const response = await fetch(fileUrl);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = blobUrl;
      const fileName = (docDetail?.name || document?.name || 'document').replace(/[^a-z0-9]/gi, '_') + '.pdf';
      link.setAttribute('download', fileName);
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Document downloaded successfully!');
    } catch (err) {
      console.error('Download Error:', err);
      toast.error('Failed to download document file.');
    }
  };

  const currentDocName = docDetail?.name || document?.name || 'Ownership Document';
  const currentTags = docDetail?.tags || docDetail?.document_tag || docDetail?.ownershipFor || document?.tags || document?.document_tag || document?.ownershipFor || '';
  const currentAddToOwnership = docDetail?.addToOwnership || (docDetail?.is_ownership ? 'Yes' : 'No') || document?.addToOwnership || 'Yes';

  return (
    <div className="document-details-page">
      {/* Loading Overlay for Songs Export */}
      <LoadingOverlay isLoading={isExporting} message="Generating Ownership Songs Export..." />

      {/* Breadcrumbs Navigation */}
      <nav className="ringtone-breadcrumb" aria-label="Breadcrumb" style={{ marginBottom: '16px' }}>
        <button 
          type="button" 
          onClick={onBack}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#64748b', fontSize: '14px', fontWeight: '500' }}
        >
          {t ? (t('ownership.breadcrumb') || 'Ownership') : 'Ownership'}
        </button>
        <span style={{ color: '#94a3b8', margin: '0 8px' }}>&gt;</span>
        <span style={{ color: '#0f172a', fontSize: '14px', fontWeight: '600' }}>
          {t ? (t('ownership.detailTitle') || 'Document View') : 'Document View'}
        </span>
      </nav>

      {/* Title */}
      <h1 className="song-title">{t ? (t('ownership.detailTitle') || 'Document view') : 'Document view'}</h1>

      {/* Document View & Meta Details (2-Column Grid matching Image 2 mockup) */}
      <div className="document-details-layout" style={{ marginTop: '20px' }}>
        {/* Left Column: Blue-bordered Card for Document Preview/Download */}
        <div 
          className="document-view-card"
          style={{ cursor: 'pointer' }}
          onClick={() => setShowDownloadConfirmModal(true)}
          title="Click to download/open document"
        >
          <div className="document-view-placeholder" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '48px', height: '48px' }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>{currentDocName}.pdf</span>
            <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '500' }}>Click to download</span>
          </div>
        </div>

        {/* Right Column: Document Meta Info */}
        <div className="document-meta-card">
          <div className="document-meta-info">
            {/* Meta Row: Document Name */}
            <div className="meta-row">
              <span className="meta-label">{t ? (t('ownership.detailDocName') || 'Document Name') : 'Document Name'}</span>
              <span className="meta-value bold-text">{currentDocName}</span>
            </div>

            {/* Meta Row: Document Tags */}
            <div className="meta-row">
              <div className="meta-row-left">
                <span className="meta-label">{t ? (t('ownership.detailDocTags') || 'Document Tags') : 'Document Tags'}</span>
                {showTags && currentTags && (
                  <div className="tags-container-inline">
                    {String(currentTags).split(',').map((tag, idx) => tag.trim() && (
                      <span key={idx} className="tag-inline-pill">{tag.trim()}</span>
                    ))}
                  </div>
                )}
              </div>
              <button 
                type="button" 
                className="toggle-tags-btn" 
                onClick={() => setShowTags(!showTags)}
              >
                {t ? (t('ownership.btnToggle') || 'Toggle') : 'Toggle'}
              </button>
            </div>

            {/* Meta Row: Add to Ownership */}
            <div className="meta-row">
              <span className="meta-label">{t ? (t('ownership.detailAddToOwnership') || 'Add to Owner ship') : 'Add to Owner ship'}</span>
              <span className="meta-value">{currentAddToOwnership}</span>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="document-meta-actions">
            <button 
              type="button" 
              className="action-btn-outlined"
              onClick={() => setShowDeleteModal(true)}
              style={{ borderColor: '#ef4444', color: '#ef4444' }}
            >
              Delete
            </button>
            <button 
              type="button" 
              className="action-btn-outlined" 
              onClick={() => setIsAddSongOpen(true)}
            >
              {t ? (t('ownership.btnAddSong') || 'Add song') : 'Add song'}
            </button>
            <button 
              type="button" 
              className="action-btn-primary" 
              onClick={() => onEditDocument && onEditDocument(docDetail || document)}
            >
              {t ? (t('ownership.btnEditDetails') || 'Edit details') : 'Edit details'}
            </button>
          </div>
        </div>
      </div>

      {/* Songs List Table Section with Search, Export & Pagination */}
      <div className="songs-list-section" style={{ marginTop: '30px' }}>
        <SharedHeader
          title={t ? (t('ownership.songsListHeader') || 'Songs List') : 'Songs List'}
          searchQuery={songsSearchQuery}
          onSearchChange={(query) => {
            setSongsSearchQuery(query);
            setSongsPage(1);
          }}
          searchPlaceholder="Search songs in document..."
          resultCount={songsTotalRecords}
          resultLabel="Songs"
          showSortFilter={false}
          onExportClick={handleExportSongs}
          onAddNewClick={() => setIsAddSongOpen(true)}
          addNewText={t ? (t('ownership.btnAddSong') || 'Add song') : 'Add song'}
        />
        
        <div className="song-table-container" style={{ marginTop: '16px' }}>
          {/* Table Headers */}
          <div className="song-table-grid song-table-header ownership-songs-grid">
            <div>{t ? (t('ownership.colSongName') || 'Song Name') : 'Song Name'}</div>
            <div>{t ? (t('ownership.colStatus') || 'Status') : 'Status'}</div>
            <div>{t ? (t('ownership.colArtist') || 'Artist') : 'Artist'}</div>
            <div>{t ? (t('ownership.colLyricsMusic') || 'Lyrics & Music') : 'Lyrics & Music'}</div>
            <div className="center-header">{t ? (t('ownership.colLabels') || 'Labels') : 'Labels'}</div>
            <div>{t ? (t('ownership.colOwnershipType') || 'Ownership Type') : 'Ownership Type'}</div>
            <div>{t ? (t('ownership.colNotesCases') || 'Notes / Cases') : 'Notes / Cases'}</div>
            <div>{t ? (t('ownership.colConflicts') || 'Conflicts') : 'Conflicts'}</div>
          </div>

          {/* Table Body */}
          <div className="song-table-body">
            {isLoadingSongs ? (
              <Loader label="Fetching document songs, please wait." />
            ) : songs.length > 0 ? (
              songs.map((song) => {
                const statusStr = (song.status === 1 || song.status === '1' || song.status === 'Active' || song.status === 'active') ? 'Active' : 'Inactive';
                const statusClass = statusStr === 'Active' ? 'active' : 'inactive';
                const hasConflict = song.conflictCount > 0 || (song.conflict && song.conflict !== 'No' && song.conflict !== '0');
                const conflictText = song.conflicts || song.conflict || (song.conflictCount > 0 ? `${song.conflictCount} Conflict` : 'No');

                return (
                  <div 
                    key={song.id} 
                    className="song-table-grid song-row ownership-songs-grid"
                    onClick={() => onSelectSong && onSelectSong(song)}
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Song Name */}
                    <div className="cell-song-name">
                      <div className="main-text" style={{ color: '#1e293b', fontWeight: '600' }}>{song.name}</div>
                      <div className="sub-text">{song.rawName || song.name}</div>
                    </div>

                    {/* Status */}
                    <div>
                      <span className={`status-pill ${statusClass}`}>
                        <span className="status-dot"></span>
                        {statusStr}
                      </span>
                    </div>

                    {/* Artist */}
                    <div>
                      <div className="main-text">{(song.artist || 'Singer').split(',').map(name => name.trim()).filter(Boolean).map(name => name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')).join(' / ')}</div>
                    </div>

                    {/* Lyrics & Music */}
                    <div>
                      <div className="main-text lyrics-text">{song.lyrics || '—'}</div>
                      <div className="sub-text music-text">{song.music || '—'}</div>
                    </div>

                    {/* Labels avatar stack */}
                    <div className="cell-labels">
                      <LabelAvatarStack labels={song.labels || song.recordLabels} />
                    </div>

                    {/* Ownership Type */}
                    <div>
                      {(() => {
                        const isSing = song.is_singer === 1 || song.is_singer === true;
                        const isRec = song.is_recordlabel === 1 || song.is_recordlabel === true;
                        const isLyr = song.is_lyrics === 1 || song.is_lyrics === true;
                        const isMus = song.is_musician === 1 || song.is_musician === true;
                        const pct = song.ownershipPercentage !== undefined ? song.ownershipPercentage : ((isRec ? 25 : 0) + (isLyr ? 25 : 0) + (isMus ? 25 : 0) + (isSing ? 25 : 0));
                        
                        return (
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                            <span 
                              className="ownership-type-pill" 
                              style={{ 
                                background: pct === 100 ? '#10b981' : pct >= 50 ? '#3b82f6' : '#f59e0b',
                                color: '#ffffff',
                                fontWeight: '700'
                              }}
                            >
                              {pct}%
                            </span>
                            {Array.isArray(song.ownershipTypes) && song.ownershipTypes.length > 0 ? (
                              song.ownershipTypes.map(typeStr => (
                                <span key={typeStr} className="ownership-type-pill">{typeStr}</span>
                              ))
                            ) : (
                              <span className="ownership-type-pill">{song.ownershipType || '—'}</span>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Notes / Cases */}
                    <div className="cell-notes">
                      {song.notes || 'No Cases Or Notes'}
                    </div>

                    {/* Conflicts */}
                    <div className="cell-conflicts">
                      <span className={`conflicts-badge ${hasConflict ? 'conflict' : 'no-conflict'}`}>
                        {conflictText}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <EmptyState 
                title={songsSearchQuery ? "No Songs Found" : "No Songs Connected"} 
                subtitle={songsSearchQuery ? "No songs match your search query." : "No songs connected to this ownership document yet."} 
                hasSearch={!!songsSearchQuery} 
              />
            )}
          </div>
        </div>

        {/* Songs List Pagination Footer */}
        {!isLoadingSongs && songs.length > 0 && (
          <Pagination
            currentPage={songsPage}
            totalPages={songsTotalPages}
            totalCount={songsTotalRecords}
            onPageChange={(page) => setSongsPage(page)}
            t={t}
          />
        )}
      </div>

      {/* Add Song Modal */}
      <AddSongModal
        isOpen={isAddSongOpen}
        onClose={() => setIsAddSongOpen(false)}
        onAddSong={handleAddSongSubmit}
        existingSongIds={songs.map(s => String(s.id || s.song_id))}
        t={t}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        type="danger"
        title="Delete Document"
        message={`Are you sure you want to delete "${currentDocName}"? This action will soft-delete the document and all associated song relationships.`}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />

      {/* Download Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDownloadConfirmModal}
        type="info"
        title="Download Document"
        message={`Are you sure you want to download "${currentDocName}"?`}
        confirmLabel="Download"
        cancelLabel="Cancel"
        onConfirm={() => {
          setShowDownloadConfirmModal(false);
          handleDownloadDocument();
        }}
        onCancel={() => setShowDownloadConfirmModal(false)}
      />
    </div>
  );
}

export default DocumentDetails;
